/**
 * Deep copy the given object considering circular structure.
 * This function caches all nested objects and its copies.
 * If it detects circular structure, use cached copy to avoid infinite loop.
 *
 * @param {*} obj
 * @param {Array<Object>} cache
 * @return {*}
 */
export function deepCopy(obj, cache = []) {
  // just return if obj is immutable value
  if (obj === null || typeof obj !== 'object') {
    return obj
  }

  // if obj is hit, it is in circular structure
  const hit = cache.filter(c => c.original === obj)[0]
  if (hit) {
    return hit.copy
  }

  const copy = Array.isArray(obj) ? [] : {}
  // put the copy into cache at first
  // because we want to refer it in recursive deepCopy
  cache.push({
    original: obj,
    copy
  })

  Object.keys(obj).forEach(key => {
    copy[key] = deepCopy(obj[key], cache)
  })

  return copy
}

// Converts date object into string with format yyyy-mm-dd
export function formatDate(date) {
  if (!date) date = new Date()
  if (date instanceof Date) {
    var dd = date.getDate()
    var mm = date.getMonth() + 1
    const yyyy = date.getFullYear()
    if (dd < 10) dd = '0' + dd
    if (mm < 10) mm = '0' + mm
    return yyyy + '-' + mm + '-' + dd
  } else {
    return date
  }
}

export function today() {
  return formatDate()
}

export function parseDate(s) {
  const ss = s.split('-')
  return new Date(ss[0], ss[1] - 1, ss[2])
}

export function dateAdd(s, days) {
  const date = parseDate(s)
  date.setDate(date.getDate() + days)
  return formatDate(date)
}

export function periodsOverlap(start, end, limitStart, limitEnd) {
  return (!start || !limitEnd || start <= limitEnd) && (!end || !limitStart || end >= limitStart)
}

export function week(s) {
  const date = parseDate(s)
  date.setDate(date.getDate() - date.getDay())
  return date
}

export function weeks(from, to) {
  let w = week(from)
  const max = week(to)
  const a = []
  while (w <= max) {
    a.push(w)
    w = new Date(w)
    w.setDate(w.getDate() + 7)
  }
  return a
}

export function weekNumber(weekStart, yearStart) {
  return Math.floor((weekStart - yearStart) / 3600 / 24 / 1000)
}

export function formatMMDD(date) {
  return '' + (date.getMonth() + 1) + '-' + date.getDate()
}

const romanMonths = ['', 'I', 'II', 'III', 'IV', 'V', 'VI', 'VII', 'VIII', 'IX', 'X', 'XI', 'XII']
export function formatWeek(weekStart) {
  const weekEnd = new Date(weekStart.getTime())
  weekEnd.setDate(weekStart.getDate() + 7)
  if (weekStart.getMonth() === weekEnd.getMonth()) {
    return '' + romanMonths[(weekStart.getMonth() + 1)] + ' ' + weekStart.getDate() + '-' + weekEnd.getDate()
  } else {
    return '' + romanMonths[(weekStart.getMonth() + 1)] + ' ' + weekStart.getDate() + '-' +
      romanMonths[(weekEnd.getMonth() + 1)] + '.' + weekEnd.getDate()
  }
}

export function dayOfWeek(d) {
  return parseDate(d).toLocaleString('pl', {
    weekday: 'long'
  })
}

const roleRank = ['P', 'RP', 'SL', 'ASL', 'L', 'AL', 'SP', 'M', 'G']
export function roleComparator(a, b) {
  const ia = roleRank.indexOf(a.role)
  const ib = roleRank.indexOf(b.role)
  return ia > ib ? 1 : ia < ib ? -1 : 0
}

export function comparator() {
  const xs = arguments
  return (a, b) => {
    for (let i = 0; i <= xs.length; i++) {
      const av = a[xs[i]]
      const bv = b[xs[i]]
      const compare = (typeof av === 'string') ? av.localeCompare(bv, 'pl') : av > bv ? 1 : av < bv ? -1 : 0
      if (compare !== 0) return compare
    }
    return 0
  }
}

export function max(a, b) {
  return a >= b ? a : b
}

export function error(code, params) {
  const message = code + (params ? '; ' + JSON.stringify(params) : '')
  const e = new Error(message)
  e.code = code
  e.params = params
  return e
}

export function promiseError(code, params) {
  return Promise.reject(error(code, params))
}

export function validateEmptyArgs(payload, ...args) {
  const emptyArgs = args.filter(key => !payload[key])
  if (emptyArgs.length) {
    return promiseError('Empty arguments', emptyArgs)
  }
}

export function objectToArray(o) {
  if (!o) return []
  return Object.keys(o).map(k => ({
    '.key': k,
    ...o[k]
  }))
}

export function arrayWithNoKeys(a) {
  return a.map(({
    '.key': key,
    ...rest
  }) => rest)
  // return a.map(o => {
  //   const o2 = {...o}
  //   delete o2['.key']
  //   return o2
  // })
}

/* eslint no-extend-native: ["error", { "exceptions": ["Array"] }] */
if (!Array.prototype.add) {
  Array.prototype.add = function (item) {
    this.push(item)
    return this
  }
}

export function copyTextToClipboard(text) {
  var textArea = document.createElement('textarea')

  //
  // *** This styling is an extra step which is likely not required. ***
  //
  // Why is it here? To ensure:
  // 1. the element is able to have focus and selection.
  // 2. if element was to flash render it has minimal visual impact.
  // 3. less flakyness with selection and copying which **might** occur if
  //    the textarea element is not visible.
  //
  // The likelihood is the element won't even render, not even a flash,
  // so some of these are just precautions. However in IE the element
  // is visible whilst the popup box asking the user for permission for
  // the web page to copy to the clipboard.
  //

  // Place in top-left corner of screen regardless of scroll position.
  textArea.style.position = 'fixed'
  textArea.style.top = 0
  textArea.style.left = 0

  // Ensure it has a small width and height. Setting to 1px / 1em
  // doesn't work as this gives a negative w/h on some browsers.
  textArea.style.width = '2em'
  textArea.style.height = '2em'

  // We don't need padding, reducing the size if it does flash render.
  textArea.style.padding = 0

  // Clean up any borders.
  textArea.style.border = 'none'
  textArea.style.outline = 'none'
  textArea.style.boxShadow = 'none'

  // Avoid flash of white box if rendered for any reason.
  textArea.style.background = 'transparent'

  textArea.value = text

  document.body.appendChild(textArea)
  textArea.focus()
  textArea.select()

  try {
    var successful = document.execCommand('copy')
    var msg = successful ? 'successful' : 'unsuccessful'
    console.log('Copying text command was ' + msg)
  } catch (err) {
    console.log('Oops, unable to copy')
  }

  document.body.removeChild(textArea)
}

export default {
  arrayWithNoKeys,
  comparator,
  copyTextToClipboard,
  dateAdd,
  error,
  formatDate,
  formatMMDD,
  formatWeek,
  objectToArray,
  periodsOverlap,
  promiseError,
  today,
  weeks,
  week,
  max,
  validateEmptyArgs
}
